import React from 'react';
import { ISelectOption, Select } from '../Select';
import { Grid } from '../Grid';
import { Button } from '../Button';
import { BodyText } from '../Typography';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { mergeTests } from '../../test-utilities/mergeTests';

interface IPagerProps extends IHasTestData {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPage: number) => void;
  disabled?: boolean;
  children?: React.ReactNode;
}
const getPageSizeOption = (i: number) => ({ label: i.toString(), value: i });
const pageSizeOptions: ISelectOption[] = [100, 50, 20].map((i) => getPageSizeOption(i));
const getPageOption = (i: number) => ({ label: (i + 1).toString(), value: i });

export const Pager: React.FC<IPagerProps> = ({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  disabled,
  'data-test-id': dataTestId,
  'data-test-class': dataTestClass,
}) => {
  const numberOfPages = Math.ceil(total / pageSize) || 1;
  const pageOptions = Array.from({ length: numberOfPages }, (_, i) => getPageOption(i));
  const maxOrderForCurrentPage = page * pageSize + pageSize;

  return (
    <Grid
      textAlign={'center'}
      alignItems={'center'}
      templateColumns={'min-content min-content 1fr min-content min-content 100px 100px'}
      data-test-class={dataTestClass}
      data-test-id={dataTestId}
    >
      <Button
        intent={'primary'}
        disabled={page === 0 || disabled}
        onClick={() => onPageChange(0)}
        data-test-class={dataTestClass}
        data-test-id={mergeTests(dataTestId, 'first')}
      >
        First
      </Button>
      <Button
        intent={'primary'}
        disabled={page === 0 || disabled}
        onClick={() => onPageChange(page - 1)}
        data-test-class={dataTestClass}
        data-test-id={mergeTests(dataTestId, 'prev')}
      >
        Prev
      </Button>
      <BodyText>
        {page * pageSize + 1} to {maxOrderForCurrentPage > total ? total : maxOrderForCurrentPage} of {total} rows
      </BodyText>
      <Button
        intent={'primary'}
        disabled={page + 1 === numberOfPages || disabled}
        onClick={() => onPageChange(page + 1)}
        data-test-class={dataTestClass}
        data-test-id={mergeTests(dataTestId, 'next')}
      >
        Next
      </Button>
      <Button
        intent={'primary'}
        disabled={page === numberOfPages - 1 || disabled}
        onClick={() => onPageChange(numberOfPages - 1)}
        data-test-class={dataTestClass}
        data-test-id={mergeTests(dataTestId, 'last')}
      >
        Last
      </Button>
      <Select
        options={pageSizeOptions}
        value={getPageSizeOption(pageSize)}
        onChange={(value: any) => onPageSizeChange(value.value)}
        isDisabled={disabled}
        data-test-class={dataTestClass}
        data-test-id={dataTestId}
      />
      <Select
        options={pageOptions}
        value={getPageOption(page)}
        onChange={(value: any) => onPageChange(value.value)}
        isDisabled={disabled}
        data-test-class={dataTestClass}
        data-test-id={dataTestId}
      />
    </Grid>
  );
};

/*
  Puts a pager above and below the children passed to it.

  This is the usual pattern for pagination so that if you are at the top or bottom, it is easy to find and use.
*/
export const PagerWrapper: React.FC<IPagerProps> = ({
  children,
  'data-test-class': dataTestClass,
  'data-test-id': dataTestId,
  ...rest
}) => (
  <>
    <Pager
      {...rest}
      data-test-class={mergeTests(dataTestClass, 'page-navigation-top')}
      data-test-id={mergeTests(dataTestId, 'page-navigation-top')}
    />
    {children}
    <Pager
      {...rest}
      data-test-class={mergeTests(dataTestClass, 'page-navigation-bottom')}
      data-test-id={mergeTests(dataTestId, 'page-navigation-top')}
    />
  </>
);
